import { CarTireFormPartModel } from "./carTireFormPartModel";
import { z } from "zod";
import { Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { tirePositions } from "../../../domain/lists/tirePositionList";
import {
  CarTireModel,
  CarTirePosition,
} from "../../../domain/models/carTireModel";
import { useCallback, useEffect } from "react";
import { requestTiresResponseModel } from "../../../../request/domain/models/requestModel";

export const CarTireFormPart = ({
  index,
  form,
  isFreestock = false,
}: CarTireFormPartModel) => {
  const { t } = useTranslation("common");

  const tireSet = form.state.values.tireSets[index];

  let tires = [] as Array<CarTireModel>;
  if (tireSet) {
    try {
      tires =
        (JSON.parse(JSON.stringify(tireSet.tires)) as Array<CarTireModel>) ||
        ([] as Array<CarTireModel>);
    } catch (e) {
      tires = [] as Array<CarTireModel>;
    }
  } else {
    tires = [] as Array<CarTireModel>;
  }

  const handleChange = (
    key: string,
    value: string | number,
    tire_id: number,
    values: CarTireModel[],
    car_position: CarTirePosition,
  ) => {
    const tire = tires.find((tire) =>
      tire.id == null
        ? tire.car_position === car_position
        : tire.id === tire_id,
    );
    if (
      tire !== undefined &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      tire[key] !== value
    ) {
      form.setFieldValue(
        `tireSets[${index}].tires`,
        values.map((tire) => {
          if (
            tire.id == null
              ? tire.car_position === car_position
              : tire.id === tire_id
          ) {
            return {
              ...tire,
              [key]: value,
            };
          }
          return tire;
        }),
      );
    } else {
      form.setFieldValue(
        `tireSets[${index}].tires`,
        values.map((tire) => {
          if (
            tire.id == null
              ? tire.car_position === car_position
              : tire.id === tire_id
          ) {
            return {
              ...tire,
              [key]: value,
            };
          }
          return tire;
        }),
      );
    }
  };

  useEffect(() => {
    if (tires && tires.length > 0) {
      tires.forEach((tire) => {
        handleChange(
          "brand",
          tire.brand as string,
          tire.id as number,
          tires,
          tire.car_position as CarTirePosition,
        );
      });
    }
  }, []);

  /**
   * Hide the tire when there is a request with delivered state and is free stock
   */
  const checkIfTireHidden = useCallback(
    (tire: CarTireModel) => {
      return tireSet?.requests?.find((request) => {
        const requestTires =
          request.requestTires as requestTiresResponseModel[];
        return (
          request.state === "delivered" &&
          isFreestock &&
          requestTires.find((t) => t.tire.id === tire.id) !== undefined
        );
      });
    },
    [tireSet, isFreestock],
  );

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <form.Field name={`tireSets[${index}].tires`}>
        {(tiresField) => {
          const tireFieldsValues = tirePositions.map((p) => {
            if (
              tiresField.state.value !== undefined &&
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              tiresField.state.value.find((t) => t.car_position === p) !==
                undefined
            ) {
              return tiresField.state.value.find(
                (t) => t.car_position === p,
              ) as CarTireModel;
            }

            return {
              car_position: p,
            } as CarTireModel;
          });

          // Add the missing tires to the list.
          if (tires && tires.length < tirePositions.length) {
            tirePositions.map((p) => {
              if (tires.find((t) => t.car_position === p) === undefined) {
                tires.push({
                  car_position: p,
                } as CarTireModel);
              }
            });
          }

          // Sort the tire in the right order.
          tires.sort((a, b) => {
            const indexA = tirePositions.indexOf(a.car_position as string);
            const indexB = tirePositions.indexOf(b.car_position as string);
            return indexA - indexB;
          });

          return (
            <>
              {tires &&
                tires.map((tire, i) => {
                  if (checkIfTireHidden(tire)) {
                    return <div className="tire-input" key={tire.id} />;
                  }
                  return (
                    <div className="tire-input" key={tire.id}>
                      <Form.Label>{t("tire." + tire.car_position)}</Form.Label>
                      <div className="tire-input-fields" key={tire.id}>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].brand`}
                          validators={{
                            onChange: z.string().optional().nullable(),
                          }}
                        >
                          {(field) => (
                            <Form.Group className="mb-3">
                              <Form.Label>{t("Brand")}</Form.Label>
                              <Form.Control
                                id={field.name}
                                name={field.name}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                type="text"
                                onChange={(e) => {
                                  handleChange(
                                    "brand",
                                    e.target.value,
                                    tire.id as number,
                                    tireFieldsValues,
                                    tire.car_position as CarTirePosition,
                                  );
                                  void field.validate("change");
                                }}
                                isInvalid={field.state.meta.errors.length > 0}
                              />
                              {field.state.meta.errors.length > 0 && (
                                <Form.Control.Feedback type="invalid">
                                  {field.state.meta.errors.join(", ")}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          )}
                        </form.Field>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].brandType`}
                          validators={{
                            onChange: z.string().optional().nullable(),
                          }}
                        >
                          {(field) => (
                            <Form.Group className="mb-3">
                              <Form.Label>{t("Brand type")}</Form.Label>
                              <Form.Control
                                id={field.name}
                                name={field.name}
                                value={field.state.value || tire.brandType}
                                onBlur={field.handleBlur}
                                type="text"
                                onChange={(e) => {
                                  handleChange(
                                    "brandType",
                                    e.target.value,
                                    tire.id as number,
                                    tireFieldsValues,
                                    tire.car_position as CarTirePosition,
                                  );
                                  void field.validate("change");
                                }}
                                isInvalid={field.state.meta.errors.length > 0}
                              />
                              {field.state.meta.errors.length > 0 && (
                                <Form.Control.Feedback type="invalid">
                                  {field.state.meta.errors.join(", ")}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          )}
                        </form.Field>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].ean`}
                          validators={{
                            onChange: z.string().optional().nullable(),
                          }}
                        >
                          {(field) => (
                            <Form.Group className="mb-3">
                              <Form.Label>{"EAN"}</Form.Label>
                              <Form.Control
                                id={field.name}
                                name={field.name}
                                value={field.state.value || tire.ean}
                                onBlur={field.handleBlur}
                                type="text"
                                onChange={(e) => {
                                  handleChange(
                                    "ean",
                                    e.target.value,
                                    tire.id as number,
                                    tireFieldsValues,
                                    tire.car_position as CarTirePosition,
                                  );
                                  void field.validate("change");
                                }}
                                isInvalid={field.state.meta.errors.length > 0}
                              />
                              {field.state.meta.errors.length > 0 && (
                                <Form.Control.Feedback type="invalid">
                                  {field.state.meta.errors.join(", ")}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          )}
                        </form.Field>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].profile`}
                          validators={{
                            onSubmit: z.number().optional().nullable(),
                          }}
                        >
                          {(field) => (
                            <Form.Group>
                              <Form.Label>{t("Profile")}</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  id={field.name}
                                  name={field.name}
                                  value={field.state.value || tire.profile}
                                  onBlur={field.handleBlur}
                                  type="number"
                                  onChange={(e) => {
                                    const parsedValue = isNaN(
                                      parseFloat(e.target.value),
                                    )
                                      ? e.target.value
                                      : parseFloat(e.target.value);
                                    handleChange(
                                      "profile",
                                      parsedValue,
                                      tire.id as number,
                                      tireFieldsValues,
                                      tire.car_position as CarTirePosition,
                                    );
                                    void field.validate("change");
                                  }}
                                  isInvalid={field.state.meta.errors.length > 0}
                                />
                                <InputGroup.Text>MM</InputGroup.Text>
                                {field.state.meta.errors.length > 0 && (
                                  <Form.Control.Feedback type="invalid">
                                    {field.state.meta.errors.join(", ")}
                                  </Form.Control.Feedback>
                                )}
                              </InputGroup>
                            </Form.Group>
                          )}
                        </form.Field>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].size`}
                          validators={{
                            onChange: z.string().optional().nullable(),
                          }}
                        >
                          {(field) => (
                            <Form.Group className="mb-3">
                              <Form.Label>{t("Size")}</Form.Label>
                              <Form.Control
                                id={field.name}
                                name={field.name}
                                value={field.state.value || tire.size}
                                onBlur={field.handleBlur}
                                type="text"
                                onChange={(e) => {
                                  handleChange(
                                    "size",
                                    e.target.value,
                                    tire.id as number,
                                    tireFieldsValues,
                                    tire.car_position as CarTirePosition,
                                  );
                                  void field.validate("change");
                                }}
                                isInvalid={field.state.meta.errors.length > 0}
                              />
                              {field.state.meta.errors.length > 0 && (
                                <Form.Control.Feedback type="invalid">
                                  {field.state.meta.errors.join(", ")}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          )}
                        </form.Field>
                        <form.Field
                          name={`tireSets[${index}].tires[${i}].run_flat`}
                        >
                          {(field) => (
                            <Form.Group>
                              <Form.Check
                                id={field.name}
                                key={field.name}
                                name={field.name}
                                checked={field.state.value || tire.run_flat}
                                onBlur={field.handleBlur}
                                onChange={(e) => {
                                  field.handleChange(e.target.checked);
                                }}
                                label={t("Run flat tire")}
                                className="mb-3"
                              />
                            </Form.Group>
                          )}
                        </form.Field>
                      </div>
                    </div>
                  );
                })}
            </>
          );
        }}
      </form.Field>
    </>
  );
};
